import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Phone, MapPin } from 'lucide-react';
import { ROUTE_SLUGS, PHONE, WHATSAPP } from '@/i18n';
import { LogoMark, OrnamentWave } from '@/components/Ornaments';

const NAV_KEYS = ['about', 'help', 'how', 'stories', 'blog', 'collaborate', 'contact'];

export const Footer = ({ lang }) => {
  const { t } = useTranslation();
  const pathFor = (key) => {
    const slug = ROUTE_SLUGS[key][lang];
    return slug ? `/${lang}/${slug}` : `/${lang}`;
  };

  return (
    <footer data-testid="site-footer" className="bg-textDark text-beige pt-20 pb-10">
      <div className="max-w-[1280px] mx-auto px-5 md:px-10">
        <div className="text-terracotta/60 mb-12">
          <OrnamentWave className="w-56 h-auto" />
        </div>
        <div className="grid md:grid-cols-[1.6fr_1fr_1fr] gap-12">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="text-terracotta w-11 h-11"><LogoMark className="w-full h-full" /></span>
              <span className="font-serif text-2xl">In Buona Compagnia</span>
            </div>
            <p className="text-beige/70 max-w-sm leading-relaxed">{t('home.footerLine')}</p>
            <p className="mt-6 font-serif italic text-terracotta text-xl">{t('footer.tagline')}</p>
          </div>

          <div>
            <h4 className="font-serif text-lg mb-4 text-terracotta">{t('nav.home')}</h4>
            <ul className="space-y-2.5">
              {NAV_KEYS.map((key) => (
                <li key={key}>
                  <Link to={pathFor(key)} data-testid={`footer-nav-${key}`} className="text-beige/75 hover:text-beige transition-colors">
                    {t(`nav.${key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-serif text-lg mb-4 text-terracotta">{t('nav.contact')}</h4>
            <a href={`tel:+39${PHONE.replace(/\s/g, '')}`} data-testid="footer-phone" className="flex items-center gap-2.5 text-beige/80 hover:text-beige mb-3 transition-colors">
              <Phone size={18} className="text-salvia" /> {PHONE}
            </a>
            <p className="flex items-center gap-2.5 text-beige/80 mb-3">
              <MapPin size={18} className="text-salvia" /> {t('contact.areaValue')}
            </p>
            <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp"
              className="inline-flex mt-2 items-center gap-2 rounded-full bg-salvia text-textDark px-5 py-2.5 font-semibold hover:brightness-105 transition-[filter]">
              WhatsApp
            </a>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-beige/15 flex flex-col md:flex-row justify-between gap-2 text-sm text-beige/55">
          <span>© {new Date().getFullYear()} In Buona Compagnia · {t('footer.rights')}</span>
          <span>{t('footer.made')}</span>
        </div>
      </div>
    </footer>
  );
};
